import { useState } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  webpSrc?: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  sizes?: string;
}

/**
 * OptimizedImage component with WebP support and fallbacks.
 * Automatically generates WebP source path if not provided.
 * Uses <picture> element for format negotiation.
 */
const OptimizedImage = ({
  src,
  webpSrc,
  alt,
  className,
  width,
  height,
  loading = 'lazy',
  priority = false,
  sizes,
}: OptimizedImageProps) => {
  const [hasError, setHasError] = useState(false);
  
  // Generate WebP path from original if not provided
  const getWebPPath = (originalPath: string): string => {
    // Replace extension with .webp
    return originalPath.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  };

  const webpSource = webpSrc || getWebPPath(src);
  const actualLoading = priority ? 'eager' : loading;
  
  // If WebP loading fails, we still have the fallback
  if (hasError) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn('transition-opacity duration-300', className)}
        width={width}
        height={height}
        loading={actualLoading}
        decoding="async"
        sizes={sizes}
      />
    );
  }

  return (
    <picture>
      <source
        srcSet={webpSource}
        type="image/webp"
        sizes={sizes}
        onError={() => setHasError(true)}
      />
      <source
        srcSet={src}
        type={src.endsWith('.png') ? 'image/png' : 'image/jpeg'}
        sizes={sizes}
      />
      <img
        src={src}
        alt={alt}
        className={cn('transition-opacity duration-300', className)}
        width={width}
        height={height}
        loading={actualLoading}
        decoding="async"
        sizes={sizes}
        onError={() => setHasError(true)}
      />
    </picture>
  );
};

export default OptimizedImage;
